import type { Column } from '@/components/Organisms/DataTable/DataTable.types';
import { colors } from '@/components/ui/theme';
import type { PriorAuth } from '@/types/benefitsVerification';
import type { Invoice, InvoiceItem } from '@/types/invoice';
import { getCurrentDate } from '@/utils/dateUtils';

/**
 * Invoice Status code mapping with colors
 */
const INVOICE_STATUS_MAP: Record<string, { label: string; color: string }> = {
	open: { label: 'Open', color: '{colors.brand.solid}' },
	paid: { label: 'Paid', color: '{colors.brand.solidSecondary}' },
	overdue: { label: 'Overdue', color: '{colors.brand.error300}' },
	pending: { label: 'Pending', color: '{colors.brand.warning300}' },
	processing: { label: 'Processing', color: '{colors.lightBlue}' },
};

/**
 * Invoice Status code mapping with colors
 */
const INVOICE_STATUS_MAP_FEED: Record<string, { label: string; color: string }> = {
	open: { label: 'Open', color: colors.primary },
	paid: { label: 'Paid', color: colors.success },
	overdue: { label: 'Overdue', color: colors.error300 },
	pending: { label: 'Pending', color: colors.warning },
	processing: { label: 'Processing', color: colors.lightBlue },
};

/**
 * Generic function to determine display text based on an array of items
 * - If length > 1: return multipleText (default: 'Multiple Items')
 * - If length === 1: return the value extracted by getDisplayValue
 * - If length === 0: return emptyText (default: '-')
 *
 * @param items - Array of items of type T
 * @param getDisplayValue - Function to extract the display value from a single item
 * @param multipleText - Text to display when multiple items exist
 * @param emptyText - Text to display when no items exist
 */
export const getDisplayFromArray = <T>(
	items: T[] | null | undefined,
	getDisplayValue: (item: T) => string,
	multipleText = 'Multiple Items',
	emptyText = '-',
): string => {
	if (!items || items.length === 0) {
		return emptyText;
	}

	if (items.length > 1) {
		return multipleText;
	}

	return getDisplayValue(items[0]);
};

/**
 * Determines product display text based on priorAuths array
 * - If length > 1: return 'Multiple Products'
 * - If length === 1: return the ta_name
 * - If length === 0: return '-'
 */
export const getProductDisplay = (priorAuths: PriorAuth[]): string => {
	return getDisplayFromArray(
		priorAuths,
		(item) => item.ta_name,
		'Multiple Products',
		'-',
	);
};

/**
 * Maps Invoice status code to status label
 * @param statusCode - The ta_Invoice_status code from API
 * @returns The status label string
 */
export const getInvoiceStatusLabel = (statusCode: string): string => {
	return INVOICE_STATUS_MAP[statusCode]?.label || '-';
};

/** * Determines the status of an invoice based on its properties
 * @param item - The invoice object
 * @return The status string: 'Pending', 'Open', 'Overdue', or 'Paid'
 **/
export const statusInvoices = (item: Invoice) => {
	if (item?.InvoiceStatus === 'pending') {
		return 'Pending';
	}
	if(item?.InvoiceStatus === 'processing') {
		return 'Processing';
	}
	if (item?.QBAmtDue > 0) {
		if (item?.pendingPayment) {
			return 'Pending';
		} else if (getCurrentDate() < item.INVOICEDUEDATE) {
			return 'Open';
		}
		return 'Overdue';
	}
	return 'Paid';
};

export const statusInvoicesFeed = (item: InvoiceItem) => {
	if (item?.invoice_InvoiceStatus === 'pending') {
		return 'Pending';
	}
	if(item?.invoice_InvoiceStatus === 'processing') {
		return 'Processing';
	}
	if (item?.invoice_QBAmtDue > 0) {
		if (item?.invoice_pendingPayment) {
			return 'Pending';
		} else if (getCurrentDate() < item.invoice_InvoiceDate) {
			return 'Open';
		}
		return 'Overdue';
	}
	return 'Paid';
};

/**
 * Maps Invoice status code to status object with label and color
 * @param statusCode - The invoice Status from API
 * @returns Object with label and color, or default values
 */
export const getInvoiceStatus = (
	statusCode: string,
): { label: string; color: string } => {
	return INVOICE_STATUS_MAP[statusCode] || { label: '-', color: '#7F8C8D' };
};

/**
 * Maps Invoice status code to status object with label and color
 * @param statusCode - The invoice Status from API
 * @returns Object with label and color, or default values
 */
export const getInvoiceStatusFeed = (
	statusCode: string,
): { label: string; color: string } => {
	return INVOICE_STATUS_MAP_FEED[statusCode] || { label: '-', color: '#7F8C8D' };
};

// Status filter options matching the image
export const statusesFilterOptions = [
	{ label: 'Open', value: 'open' },
	{ label: 'Processing', value: 'processing' },
	{ label: 'Overdue', value: `past_due,${getCurrentDate()}` },
	{ label: 'Paid', value: 'paid' },
];

export const statusesFilterOptionsForOpen = [
	{ label: 'Good to pay', value: `open,0||gross_margin_value,positive` },
	{ label: 'Billed (waiting for payment)', value: 'open,0||billed_data,true' },
	{ label: 'Not Billed', value: 'open,0||billed_data,false' },
	{ label: 'Hold', value: 'open,0||gross_margin_value,negative' },
];

// Columns matching the image
export const columns: Column[] = [
	{
		key: 'Clinic',
		label: 'Clinic',
		width: '200px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'Invoice',
		label: 'Invoice No.',
		width: '100px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'State',
		label: 'State',
		width: '50px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'Product',
		label: 'Product',
		width: '200px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'ProductID',
		label: 'Tissue ID',
		width: '130px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'QBAmtDue',
		label: 'Balance',
		width: '80px',
		type: 'number',
		sorter: true,
	},
	{
		key: 'InvoiceDate',
		label: 'Invoice Date',
		width: '100px',
		type: 'date',
		sorter: true,
	},
	{
		key: 'PatientLastName',
		label: 'Patient Name',
		width: '120px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'Invoicecollected',
		label: 'Collection',
		width: '80px',
		type: 'number',
		sorter: true,
	},
	{
		key: 'GrossMargin',
		label: 'Gross',
		width: '80px',
		type: 'number',
		sorter: true,
	},
	{
		key: 'InvoiceStatus',
		label: 'Status',
		width: '140px',
		type: 'string-status',
		filterOptions: statusesFilterOptions,
		// sorter: true,
	},
	{
		key: 'Aging',
		label: 'Aging',
		width: '80px',
		type: 'number',
		sorter: true,
	},
];

export const INVOICE_TYPE: Record<string, string> = {
	open: 'open',
	all: 'all',
	paid: 'paid',
};
